import { installCompleteCartAdapter } from './cart-complete';
import { formatList } from '../../../app/Libraries/Utils';
import { cartCheckoutUrl, cartHasUnavailableItems } from './cart-state';
import { cartAvailabilityEnabled, checkCartAvailability, cartAvailabilityAllowsCheckout, recordCartAvailabilityNotice, type CartAvailabilityResult } from './cart-availability';
import { merchantItemIdentity, browserMerchantTrackingContext } from '../../../app/Libraries/MerchantTracking';
import { stardust } from '@eidellev/adonis-stardust/client';

import $ from 'jquery';
import littledata from '@littledata/headless-shopify-sdk';
import aa from './algolia-insights';

import {
  formatAsMoney,
  extractID,
  getPageLang,
} from './utils';

import updateVariantImage from './product/update-variant-image';
import ShopifyWrapper, { type CustomAttribute } from '@him-her/shopify-wrapper';

import i18next, { type TFunction } from 'i18next';
import i18nextInstance from './i18next';
import { trackSearchEvent } from './search-events';
import { signatureSansEnabled, signatureVariantId, runSignatureCardAdd } from './signature-sans-cards';
import { signatureHomepageEnabled } from './signature-homepage-cards';
import { signatureCatalogueEnabled, signatureCatalogueFreshEligible } from './signature-catalogue-cards';
let t: TFunction;

const domain = process.env.SHOPIFY_DOMAIN || '';
const storefrontAccessToken = process.env.SHOPIFY_STOREFRONT_TOKEN || '';
let client: ShopifyWrapper;
let cart;

// TODO: Move these to env var?
const PRODUCTS_INDEX = 'shopify_products';
const DEFAULT_INDEX = PRODUCTS_INDEX;

let signatureCartInitPromise: Promise<void> | null = null;
let signatureCardPending = false;
document.addEventListener('DOMContentLoaded', () => {
  if (signatureSansEnabled() || signatureCatalogueEnabled() || signatureHomepageEnabled()) bindSignatureCardButtons();
  signatureCartInitPromise = initializeCart();
});
let cartInitializing = false;
async function initializeCart() {
  if (cartInitializing) return;
  cartInitializing = true;
  const nativeProduct = nativePurchaseProduct();
  if (nativeProduct) {
    const footer = editorialFooter(nativeProduct);
    if (footer) footer.dataset.cartReady = 'false';
    const available = nativeProduct.dataset.purchaseAvailable === undefined ? nativePurchaseButtons(nativeProduct).some(button => !button.hasAttribute('disabled')) : nativeProduct.dataset.purchaseAvailable === 'true';
    nativeProduct.dataset.purchasePending = 'true';
    nativePurchaseAvailability(nativeProduct, available);
    nativePurchaseMessage(nativeProduct, '');
  }
  // Testing new wrapper
  // const wrapper = new ShopifyWrapper({
  //   domain,
  //   token: storefrontAccessToken,
  //   metafields: {
  //     product: [{
  //       name: 'publicSku',
  //       namespace: 'custom',
  //       key: 'public_sku',
  //     }],
  //     variant: [{
  //       name: 'publicSku',
  //       namespace: 'custom',
  //       key: 'public_sku',
  //     }],
  //   },
  //  debug: true,
  // });
  // await wrapper.test.cart();
  // debugger;

  try {
    // Load i18next
    t = await i18nextInstance;
    await i18next.loadNamespaces(['general']);

    client = new ShopifyWrapper({
      domain,
      token: storefrontAccessToken,
      apiVersion: '2026-10',
      country: 'CA',
      language: getPageLang().toUpperCase(),
      metafields: {
        product: [{
          name: 'publicSku',
          namespace: 'custom',
          key: 'public_sku',
        }],
        variant: [{
          name: 'publicSku',
          namespace: 'custom',
          key: 'public_sku',
        }],
      },
    });

    installCompleteCartAdapter(client);

    // Check if a cart already exists in local storage
    const cartId = localStorage.getItem('lastCartId');
    // Try and fetch the cart if we have an ID
    if (cartId) {
      cart = await client.cart.fetch(cartId);
    }

    if (!cart) {
      // If cart wasn't found, or we didn't have an ID, create a new cart
      cart = await client.cart.create();
    }

    if (nativeProduct && (!cart?.id || !Array.isArray(cart.lineItems))) throw new Error('Cart unavailable');

    // Validate current online tags before rendering a restored bag.
    const restoredAvailability = cartAvailabilityEnabled() ? await validateCurrentCart() : null;

    // render the new cart
    localStorage.setItem('lastCartId', cart.id);

    const editorial = nativeProduct && editorialFooter(nativeProduct);
    if (editorial) editorial.dataset.cartReady = 'true';

    updateCartTabButton();

    if (nativeProduct) { nativeProduct.dataset.purchasePending = 'false'; nativePurchaseAvailability(nativeProduct, nativeProduct.dataset.purchaseAvailable === 'true'); }
    bindEventListeners();
    initVariantSelectors();
    if (restoredAvailability?.removed) showCartAvailabilityNotice();

    // Add littledata client IDs to Shopify cart
    if (typeof Cookiebot !== 'undefined' && Cookiebot?.consent.statistics) {
      // Optional analytics must not prevent the purchase controls from binding.
      try {
      // https://www.npmjs.com/package/@littledata/headless-shopify-sdk?activeTab=readme#using-the-shopify-cart-api
      await littledata
        .fetchClientIds({
          // cSpell:disable-next-line
          ga4MeasurementId: 'G-0QLGEFWDB3',
          fbPixelId: '306601538442852',
        });
      // console.debug('littledata ids', clientIds);

      // Send cart info to littledata
      const noteAttributes = await littledata.sendCartToLittledata(cart.id);
      await client.cart.updateAttributes(cart.id, noteAttributes);
      } catch { /* Purchase flow remains available without analytics. */ }
    }


  } catch (err) {
    console.error(err);
    if (nativeProduct) {
      nativeProduct.dataset.purchasePending = 'false';
      nativePurchaseButtons(nativeProduct).forEach(button => button.setAttribute('disabled', 'disabled'));
      const french = getPageLang() === 'fr';
      nativePurchaseRetry(nativeProduct, typeof t === 'function' ? t('general:product.bagLoadError') : french ? 'Votre panier n’a pas pu être chargé.' : 'Your bag could not be loaded.', typeof t === 'function' ? t('general:product.tryAgain') : french ? 'Réessayer' : 'Try again', initializeCart);
    }
  } finally { cartInitializing = false; }
}

function initVariantSelectors() {
  if (!document.querySelector('main.product')) {
    return;
  }

  if (!document.querySelector('.js-variant-selector')) {
    return;
  }

  const selectedVariants = document.querySelectorAll<HTMLInputElement>('.js-variant-selector input:checked');

  selectedVariants.forEach((selectedVariant) => {
    const name = selectedVariant.dataset.option as string;
    const value = selectedVariant.value;
    const handle = selectedVariant.dataset.product as string;

    client.product.fetchByHandle(handle).then((product) => {
      showAvailableVariants(name, value, product);
    });
  });
}

/**
 * Bind Event Listeners
 */
/**
 * Bind the directly-bound PDP buy buttons. Guarded with data-cart-bound so
 * it can safely re-run when the PDP colourway swap re-renders the buttons.
 */
function bindProductBuyButtons() {
  const editorialProduct = nativePurchaseProduct();
  if (editorialProduct && editorialFooter(editorialProduct)) {
    renderEditorialPurchase(editorialProduct, undefined, '');
    if (!editorialProduct.hasAttribute('data-editorial-options-bound')) {
      editorialProduct.setAttribute('data-editorial-options-bound', 'true');
      editorialProduct.addEventListener('change', (event) => {
        const target = event.target as HTMLElement;
        if (!target.closest('.js-variant-selector, .single-product__ring-size-selectors')) return;
        invalidateTitleSignatureSelection(editorialProduct);
        const footer = editorialFooter(editorialProduct);
        if (footer) editorialConfirmations.delete(footer);
        renderEditorialPurchase(editorialProduct, undefined, '');
      });
    }
  }
  /* buy button click listener for single product pages */

  let addToCartBtns = document.querySelectorAll<HTMLElement>('.product .js-add-to-cart:not([data-cart-bound])');

  addToCartBtns.forEach((btn) => {
    btn.setAttribute('data-cart-bound', 'true');
    btn.addEventListener('click', async (e) => {
      e.preventDefault();

      if (!nativePurchaseProduct()) {
        btn.setAttribute('disabled', 'true');
        btn.classList.add('processing');
      }

      const variantID = btn.dataset.id as string;

      let customAttributes: CustomAttribute[] = [];

      let ringSizeSelectors = document.querySelector('.single-product__ring-size-selectors');

      if (ringSizeSelectors) {
        const ringSize = ringSizeSelectors.querySelector<HTMLInputElement>('input:checked')?.dataset.ringsize;

        customAttributes.push({
          key: 'Size',
          value: ringSize || '',
        });
      }

      const product = nativePurchaseProduct();
      if (btn.hasAttribute('data-editorial-add-only') && (!product || !editorialFooter(product)?.hasAttribute('data-editorial-dual-actions'))) return;
      if (product && btn.classList.contains('js-editorial-purchase') && !btn.hasAttribute('data-editorial-add-only') && editorialConfirmation(product)) {
        await editorialCheckout(product);
      } else {
        await buyButtonClickHandler(variantID, customAttributes);
      }
    });
  });

  const quickBuyBtns = document.querySelectorAll<HTMLElement>('.js-buy-now:not([data-cart-bound])');

  quickBuyBtns.forEach((btn) => {
    btn.setAttribute('data-cart-bound', 'true');
    btn.addEventListener('click', async (e) => {
      e.preventDefault();

      if (!nativePurchaseProduct()) {
        btn.setAttribute('disabled', 'true');
        btn.classList.add('processing');
      }

      const variantID = btn.dataset.id as string;

      let customAttributes: CustomAttribute[] = [];

      const ringSizeSelectors = document.querySelector('.single-product__ring-size-selectors');

      if (ringSizeSelectors) {
        const ringSize = ringSizeSelectors.querySelector<HTMLInputElement>('input:checked')?.dataset.ringsize || '';

        customAttributes.push({
          key: 'ringSize',
          value: ringSize,
        });
      }

      const product = nativePurchaseProduct();
      if (btn.hasAttribute('data-editorial-buy-now')) {
        if (product) await editorialBuyNow(product, variantID);
      } else if (product && btn.hasAttribute('data-title-signature-buy') && titleSignatureEnabled(product)) {
        await titleSignatureBuyNow(product, variantID);
      } else {
        await buyButtonClickHandler(variantID, customAttributes, true);
      }
    });
  });
}

function bindEventListeners() {
  bindProductBuyButtons();
  // The PDP colourway swap replaces the buy buttons — bind the fresh ones
  document.addEventListener('product:swap', () => {
    const product = nativePurchaseProduct();
    const footer = product && editorialFooter(product);
    if (footer && product) {
      invalidateTitleSignatureSelection(product);
      editorialConfirmations.delete(footer);
      delete product.dataset.stockVariant;
      nativePurchaseAvailability(product, footer.dataset.editorialPurchasable === 'true');
    }
    bindProductBuyButtons();
  });

  // collection add to carts
  // TODO: figure out how to export the function so that its not run multiple times on instantsearch.js file

  $('.collection, .search-results__product-grid, .product-list, .featured-product')
    .on('click', '.js-add-to-cart', async (e) => {
      const btn = e.currentTarget;

      const variantID = btn?.dataset.id;

      // btn.setAttribute('disabled', 'true');
      btn.classList.add('processing');

      await buyButtonClickHandler(variantID, []);
    });

  let variantSelectors = document.querySelectorAll('.js-variant-selector');

  variantSelectors.forEach((selector) => {
    let input = selector.querySelector<HTMLInputElement>('input');
    if (!input) {
      return;
    }

    input.addEventListener('change', () => {
      const name = input!.dataset.option as string;
      const value = input!.value;
      const quantity = 1;
      const handle = input!.dataset.product as string;

      const allInputs = document.querySelectorAll<HTMLInputElement>(`.js-variant-selector input[data-product="${handle}"]:checked`);

      const selectedOptions = [...allInputs].map((input) => {
        return {
          name: input.dataset.option as string,
          value: input.value,
        };
      });

      // update label if it exists
      const label = document.querySelector(`.js-selected-option[data-option="${name}"]`);

      if (label) {
        label.innerHTML = `${name}: ${value}`;
      }

      const native = nativePurchaseProduct();
      const footer = native?.dataset.product === handle ? editorialFooter(native) : null;
      const request = footer ? String(Number(footer.dataset.optionRequest || 0) + 1) : '';
      if (footer && native) {
        footer.dataset.optionRequest = request; footer.dataset.optionPending = 'true';
        editorialConfirmations.delete(footer); nativePurchaseAvailability(native, false);
      }
      const current = () => !footer || !!native && editorialFooter(native) === footer && footer.dataset.optionRequest === request && native.dataset.product === handle;
      client.product.fetchByHandle(handle).then((product) => {
        if (!current()) return;
        if (footer) footer.dataset.optionPending = 'false';
        setSelectedVariant(name, value, product, quantity, selectedOptions);
      }).catch(() => {
        if (!footer || !native || !current()) return;
        footer.dataset.optionPending = 'false';
        nativePurchaseRetry(native, t('general:product.availabilityError'), t('general:product.tryAgain'), () => input!.dispatchEvent(new Event('change', { bubbles: true })));
      });
    });
  });
}

/** Guarded card surfaces share the existing cart, never checkout. */
function bindSignatureCardButtons() {
  const root = document.querySelector<HTMLElement>('main[data-signature-sans-cards][data-watch-buying="collection"], main[data-signature-catalogue], section#home-watch-discovery[data-signature-home][data-watch-buying="home"]');
  if (!root || root.dataset.signatureCartBound === 'true') return;
  root.dataset.signatureCartBound = 'true';
  root.addEventListener('click', event => {
    const button = (event.target as Element)?.closest<HTMLButtonElement>('button.js-signature-add');
    if (!button || !root.contains(button)) return;
    event.preventDefault();
    void handleSignatureCardBuy(button);
  });
}

export async function handleSignatureCardBuy(button: HTMLButtonElement): Promise<boolean> {
  if ((!signatureSansEnabled() && !signatureCatalogueEnabled() && !signatureHomepageEnabled()) || signatureCardPending || button.disabled || !button.isConnected) return false;
  const card = button.closest<HTMLElement>('[data-signature-card]');
  const variantId = signatureVariantId(button.dataset.id);
  const handle = card?.dataset.product || '';
  if (!card || !variantId || signatureVariantId(card.dataset.objectId) !== variantId
    || !card.closest('main[data-signature-sans-cards][data-watch-buying="collection"], main[data-signature-catalogue], section#home-watch-discovery[data-signature-home][data-watch-buying="home"]')) return false;
  const label = button.querySelector<HTMLElement>('[data-signature-bag-label]');
  const status = card.querySelector<HTMLElement>('[data-signature-status]');
  const catalogueCard = card.matches('.rf-signature-piece') && !!card.closest('main[data-signature-catalogue], section#home-watch-discovery[data-signature-home][data-watch-buying="home"]');
  const french = getPageLang() === 'fr';
  const copy = (key: string, en: string, fr: string) => typeof t === 'function' ? String(t(key)) : french ? fr : en;
  const addLabel = () => copy('general:watchJourney.addToBag', 'Add to bag', 'Ajouter au panier');
  const current = () => button.isConnected && button.closest('[data-signature-card]') === card
    && card.dataset.product === handle && signatureVariantId(button.dataset.id) === variantId
    && signatureVariantId(card.dataset.objectId) === variantId;
  signatureCardPending = true;
  card.dataset.signaturePending = 'true';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (label) label.textContent = copy('general:signatureCards.adding', 'Adding…', 'Ajout en cours…');
  if (status) { status.hidden = true; status.textContent = ''; status.setAttribute('role', 'status'); }
  let succeeded = false;
  try {
    if (signatureCartInitPromise) await signatureCartInitPromise;
    if (!client || !cart?.id) {
      signatureCartInitPromise = initializeCart();
      await signatureCartInitPromise;
    }
    if (!client || !cart?.id) throw new Error('Cart unavailable');
    const result = await runSignatureCardAdd({ handle, variantId }, {
      fetchCart: () => client.cart.fetch(cart.id),
      fetchProduct: async selectedHandle => {
        const product = await client.product.fetchByHandle(selectedHandle);
        if (catalogueCard && !signatureCatalogueFreshEligible(product, variantId)) throw new Error('Options require review');
        return product;
      },
      fetchStock: async selectedVariant => {
        const response = await fetch(stardust.route('api.shop.stock', undefined, { qs: { id: selectedVariant, lang: getPageLang() } }), {
          credentials: 'same-origin', signal: AbortSignal.timeout(10000),
        });
        if (!response.ok) throw new Error('Stock unavailable');
        return (await response.json()).result;
      },
      addItem: (cartId, selectedVariant) => client.cart.addLineItems(cartId, [{ variantId: selectedVariant, quantity: 1, customAttributes: [] }]),
      isCurrent: current,
    });
    cart = result.cart;
    succeeded = true;
    // Both are best-effort follow-ups. Cart confirmation must not depend on analytics.
    try { updateCartTabButton(); } catch { /* The confirmed cart is still valid. */ }
    void buyButtonAnalytics(result.line, result.cart, card).catch(() => undefined);
    if (current()) {
      const added = copy('general:product.addedToBag', 'Added to bag', 'Ajouté au panier');
      if (label) label.textContent = added;
      if (status) { status.textContent = added; status.hidden = false; }
      setTimeout(() => { if (current() && !button.disabled && label) label.textContent = addLabel(); }, 4000);
    }
    return true;
  } catch {
    if (current() && status) {
      status.setAttribute('role', 'alert');
      status.textContent = copy('general:product.purchaseError', 'This piece could not be added. Please try again.', 'Cette pièce n’a pas pu être ajoutée. Veuillez réessayer.');
      status.hidden = false;
    }
    return false;
  } finally {
    signatureCardPending = false;
    delete card.dataset.signaturePending;
    if (current()) {
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
      if (!succeeded && label) label.textContent = addLabel();
    }
  }
}

/**
 * Update cart tab button
 */
function updateCartTabButton() {
  if (!cartAvailabilityEnabled()) {
    client.cart
      .fetch(cart.id)
      .then((cart) => {
        const cartLinks = document.querySelectorAll('.js-nav-cart');
        if (cart.lineItems.length > 0) {
          cartLinks.forEach((link) => {
            link.classList.add('has-items');
          });
        } else {
          cartLinks.forEach((link) => {
            link.classList.remove('has-items');
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
    return;
  }
  if (document.querySelector('main.cart')) return; // The bag session owns its rendered totals.
  // The current validated/mutation response is authoritative. An independent
  // older fetch must never repaint a badge after app and bag cleanup finish.
  const quantity = (cart?.lineItems || []).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  document.querySelectorAll('.js-nav-cart').forEach(link => link.classList.toggle('has-items', quantity > 0));
  document.querySelectorAll<HTMLElement>('.js-cart-num').forEach(node => { node.textContent = String(quantity); node.classList.toggle('active', quantity > 0); });
}
function showCartAvailabilityNotice(message = t('general:cart.availabilityRemoved')) {
  if (!cartAvailabilityEnabled() || document.querySelector('main.cart')) return;
  const product = nativePurchaseProduct();
  if (product) { nativePurchaseMessage(product, message, false); return; }
  let notice = document.querySelector<HTMLElement>('[data-cart-availability-notice]');
  if (!notice) {
    const main = document.querySelector('main');
    if (!main) return;
    notice = document.createElement('aside'); notice.setAttribute('data-cart-availability-notice', '');
    notice.setAttribute('role', 'status'); notice.setAttribute('aria-live', 'polite');
    notice.style.cssText = 'margin:16px auto;padding:16px;max-width:1200px;background:#f7f5ef;border:1px solid #d6d0c4;color:#17243b;font-size:14px;line-height:1.5';
    main.prepend(notice);
  }
  const text = document.createElement('p'); text.textContent = message;
  const bag = document.querySelector<HTMLAnchorElement>('a.js-nav-cart[href]');
  notice.replaceChildren(text);
  if (bag) { const link = document.createElement('a'); link.href = bag.href; link.textContent = t('general:cart.shoppingBag'); link.style.textDecoration = 'underline'; notice.append(link); }
}
async function validateCurrentCart(): Promise<CartAvailabilityResult> {
  const result = await checkCartAvailability(cart, client, { storeKey: domain });
  if (result.cart?.id && Array.isArray(result.cart.lineItems)) cart = result.cart;
  updateCartTabButton();
  if (result.removed) { recordCartAvailabilityNotice(cart.id, domain); showCartAvailabilityNotice(); }
  return result;
}
async function validateQuickCheckout(variantID: string): Promise<boolean> {
  if (!cartAvailabilityEnabled()) return true;
  const result = await validateCurrentCart();
  if (cartAvailabilityAllowsCheckout(result, variantID)) return true;
  // Do not replace a truthful cleanup notice with a generic success/error.
  if (!result.removed) {
    const product = nativePurchaseProduct();
    if (product) nativePurchaseMessage(product, t('general:product.checkoutError'));
    else showCartAvailabilityNotice(t('general:product.checkoutError'));
  }
  return false;
}

/**
 * Attach and control listeners onto buy button
 */
async function legacyBuyButtonClickHandler(
  variantID: string,
  customAttributes: CustomAttribute[] = [],
  quickBuy: boolean = false
) {
  const quantity = 1;

  const lineItemsToAdd = [
    {
      variantId: variantID,
      quantity,
      customAttributes: customAttributes,
    },
  ];

  const btns = document.querySelectorAll<HTMLElement>(`.js-add-to-cart[data-id="${variantID}"]`);
  const productCard = btns[0]?.closest('.js-product');

  // Add an item to the cart
  return client.cart
    .addLineItems(cart.id, lineItemsToAdd)
    .then((newCart) => {
      cart = newCart;
      return newCart;
    })
    .then(async () => {
      updateCartTabButton();

      const SUCCESS_TIMEOUT_MS = 4000;

      btns.forEach((btn) => {
        btn.removeAttribute('disabled');
        btn.classList.remove('processing');
        btn.classList.add('success');

        // If it's the product page, show success state
        if (btn.classList.contains('single-product__bag-btn')) {
          btn.innerText = t('general:product.addedToBag');
        } else {
          // If not on product page, remove success class after timeout
          setTimeout(() => {
            btn.classList.remove('success');
          }, SUCCESS_TIMEOUT_MS);
        }
      });

      let variant;

      cart.lineItems.forEach((product) => {
        // check that the product still exists in Shopify
        if (product.variant.id === variantID) {
          variant = product;
        }
      });

      await buyButtonAnalytics(variant, cart, productCard);

      // If quick buy, redirect to checkout
      if (quickBuy) {
        if (!await validateQuickCheckout(variantID)) return false;
        window.open(cart.webUrl, '_self');
      }

      return true;
    })
    .catch((err) => {
      console.error(err);
      btns.forEach((btn) => {
        btn.removeAttribute('disabled');
        btn.classList.remove('processing');
      });
    });
}

/** The server emits this footer only for approved releases. Never activate on protected pages. */
type EditorialConfirmation = { variantID: string; signature: string; lineId: string; attributes: CustomAttribute[] };
const editorialConfirmations = new WeakMap<HTMLElement, EditorialConfirmation>();
function editorialFooter(product: HTMLElement): HTMLElement | null {
  if (!['raffi-jewellers.ca', 'www.raffi-jewellers.ca', 'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com'].includes(window.location.hostname) ||
      /rolex|tag[\s-]*heuer/i.test(product.dataset.brand || '') ||
      /(?:^|[/-])rolex(?:[-/]|$)/i.test(window.location.pathname)) return null;
  return product.querySelector<HTMLElement>('[data-editorial-sticky]');
}
/** The Title Signature marker is emitted only for the guarded native PDP. */
function titleSignatureEnabled(product: HTMLElement): boolean {
  return !!editorialFooter(product) && product.closest('main[data-title-signature-product]')?.querySelector('.js-product') === product;
}
function invalidateTitleSignatureSelection(product: HTMLElement) {
  if (titleSignatureEnabled(product)) product.dataset.titleSignatureRevision = String(Number(product.dataset.titleSignatureRevision || 0) + 1);
}
function renderTitleSignaturePurchase(product: HTMLElement) {
  if (!titleSignatureEnabled(product)) return;
  const shipping = product.querySelector<HTMLElement>('[data-title-signature-shipping]');
  if (shipping) {
    const price = Number(shipping.dataset.shippingPrice);
    const stockConfirmed = product.dataset.stockVariant ? editorialFooter(product)?.dataset.stockLoading !== 'true' && product.dataset.purchaseAvailable === 'true' : shipping.dataset.shippingEligible === 'true';
    const eligible = shipping.dataset.shippingPurchasable === 'true' && stockConfirmed && product.dataset.purchaseAvailable !== 'false' && Number.isFinite(price) && price > 0;
    shipping.dataset.shippingEligible = String(eligible);
    shipping.textContent = !eligible ? shipping.dataset.labelUnknown || '' : price >= Number(shipping.dataset.shippingThreshold || 750) ? shipping.dataset.labelComplimentary || '' : shipping.dataset.labelBelow || '';
  }
  const button = product.querySelector<HTMLButtonElement>('[data-title-signature-buy]');
  const label = button?.querySelector<HTMLElement>('[data-title-signature-buy-label]');
  if (button && label) label.textContent = product.dataset.titleSignatureCheckout === 'true' ? button.dataset.labelOpeningCheckout || '' : button.dataset.labelBuyNow || '';
}
/** A direct purchase reuses the selected cart line and never clears existing bag contents or notes. */
async function titleSignatureBuyNow(product: HTMLElement, variantID: string) {
  if (!titleSignatureEnabled(product) || product.dataset.purchasePending === 'true' || product.dataset.purchaseAvailable === 'false') return false;
  const selection = editorialSelection(product);
  if (!variantID || selection.variantID !== variantID) return false;
  product.dataset.titleSignatureCheckout = 'true';
  renderTitleSignaturePurchase(product);
  try {
    if (editorialConfirmation(product)) return await editorialCheckout(product);
    return await buyButtonClickHandler(variantID, selection.customAttributes, true);
  } finally {
    delete product.dataset.titleSignatureCheckout;
    renderTitleSignaturePurchase(product);
  }
}
/** Separate sticky actions keep Add to bag additive; Buy now reuses its confirmed selection and guarded checkout. */
async function editorialBuyNow(product: HTMLElement, variantID: string) {
  const footer = editorialFooter(product);
  if (!footer?.hasAttribute('data-editorial-dual-actions') || footer.dataset.editorialPurchasable !== 'true' ||
      product.dataset.purchasePending === 'true' || product.dataset.purchaseAvailable === 'false' ||
      footer.dataset.cartReady === 'false' || footer.dataset.stockLoading === 'true' || footer.dataset.optionPending === 'true') return false;
  const selection = editorialSelection(product);
  if (!variantID || selection.variantID !== variantID) return false;
  if (titleSignatureEnabled(product)) return titleSignatureBuyNow(product, variantID);
  if (editorialConfirmation(product)) return editorialCheckout(product);
  // Use the same Size attributes as Add to bag, including jewellery ring selections.
  return buyButtonClickHandler(variantID, selection.customAttributes, true);
}
function editorialAttributesKey(attributes: CustomAttribute[] = []): string {
  return JSON.stringify(attributes.map(attribute => [String(attribute.key), String(attribute.value)]).sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1])));
}
function editorialSelection(product: HTMLElement) {
  const footer = editorialFooter(product);
  const variantID = footer?.querySelector<HTMLElement>('.js-editorial-purchase')?.dataset.id || '';
  const ringSizes = product.querySelector('.single-product__ring-size-selectors');
  const customAttributes: CustomAttribute[] = ringSizes ? [{ key: 'Size', value: ringSizes.querySelector<HTMLInputElement>('input:checked')?.dataset.ringsize || '' }] : [];
  return { variantID, customAttributes, signature: JSON.stringify([product.dataset.product, variantID, editorialAttributesKey(customAttributes)]) };
}
function editorialConfirmation(product: HTMLElement): EditorialConfirmation | null {
  const footer = editorialFooter(product);
  const confirmed = footer && editorialConfirmations.get(footer);
  return confirmed && confirmed.signature === editorialSelection(product).signature ? confirmed : null;
}
function renderEditorialPurchase(product: HTMLElement, phase?: string, message?: string, error = false) {
  renderTitleSignaturePurchase(product);
  const footer = editorialFooter(product);
  if (!footer) return;
  const button = footer.querySelector<HTMLButtonElement>('.js-editorial-purchase');
  const inquiry = footer.querySelector<HTMLButtonElement>('[data-editorial-inquiry]');
  const dual = footer.hasAttribute('data-editorial-dual-actions');
  const confirmed = editorialConfirmation(product);
  const pending = product.dataset.purchasePending === 'true';
  const unavailable = product.dataset.purchaseAvailable === 'false' && !pending && footer.dataset.stockLoading !== 'true' && footer.dataset.optionPending !== 'true';
  const state = phase || (pending ? footer.dataset.editorialState === 'checking-out' ? 'checking-out' : footer.dataset.editorialState === 'adding' ? 'adding' : 'ready' : confirmed ? 'added' : 'ready');
  footer.dataset.editorialState = state;
  if (button) {
    button.hidden = unavailable;
    button.disabled = pending || product.dataset.purchaseAvailable === 'false' || footer.dataset.cartReady === 'false' || footer.dataset.optionPending === 'true' || dual && footer.dataset.stockLoading === 'true';
    button.setAttribute('aria-busy', String(pending || dual && (footer.dataset.stockLoading === 'true' || footer.dataset.optionPending === 'true')));
    const label = button.querySelector('[data-editorial-label]');
    if (label) label.textContent = dual ? state === 'adding' ? footer.dataset.labelAdding || '' : footer.dataset.labelAdd || '' : state === 'checking-out' ? footer.dataset.labelCheckingOut || '' : state === 'adding' ? footer.dataset.labelAdding || '' : confirmed ? footer.dataset.labelCheckout || '' : footer.dataset.labelAdd || '';
  }
  if (inquiry) inquiry.hidden = !unavailable;
  if (dual) {
    const buy = footer.querySelector<HTMLButtonElement>('[data-editorial-buy-now]');
    if (buy) {
      buy.hidden = unavailable;
      buy.disabled = pending || product.dataset.purchaseAvailable === 'false' || footer.dataset.cartReady === 'false' || footer.dataset.stockLoading === 'true' || footer.dataset.optionPending === 'true';
      buy.setAttribute('aria-busy', String(pending || footer.dataset.stockLoading === 'true' || footer.dataset.optionPending === 'true'));
      const label = buy.querySelector('[data-editorial-buy-label]');
      if (label) label.textContent = state === 'checking-out' || product.dataset.titleSignatureCheckout === 'true' ? footer.dataset.labelCheckingOut || '' : buy.dataset.labelBuyNow || '';
    }
    const contact = footer.querySelector<HTMLButtonElement>('[data-editorial-contact]');
    if (contact) contact.hidden = footer.dataset.editorialPurchasable === 'true' && !unavailable;
  }
  const price = Number(footer.querySelector<HTMLElement>('.js-product-price')?.dataset.price ?? footer.dataset.editorialPrice);
  const shipping = footer.querySelector('[data-editorial-shipping]');
  if (shipping && footer.dataset.editorialPurchasable === 'true') shipping.textContent = unavailable ? footer.dataset.labelAvailability || '' : price >= 750 ? footer.dataset.labelShippingComplimentary || '' : footer.dataset.labelShippingBelow || '';
  const status = footer.querySelector<HTMLElement>('[data-editorial-status]');
  if (status && message !== undefined) {
    status.textContent = message; status.hidden = !message;
    status.setAttribute('role', error ? 'alert' : 'status');
    status.classList.toggle('editorial-sticky__status--error', error);
  }
}
/** A checkout retry only reads the confirmed cart; it never adds or overwrites a gift note. */
async function editorialCheckout(product: HTMLElement) {
  const footer = editorialFooter(product), confirmed = editorialConfirmation(product);
  if (!footer || !confirmed || product.dataset.purchasePending === 'true' || product.dataset.purchaseAvailable === 'false') return false;
  const revision = product.dataset.titleSignatureRevision || '0';
  const current = () => editorialFooter(product) === footer && editorialConfirmation(product) === confirmed && nativePurchaseProduct() === product && (!titleSignatureEnabled(product) || (product.dataset.titleSignatureRevision || '0') === revision);
  product.dataset.purchasePending = 'true';
  nativePurchaseAvailability(product, true);
  renderEditorialPurchase(product, 'checking-out', '');
  try {
    let latest = await client.cart.fetch(cart.id);
    if (!current()) return false;
    if (!latest?.id || !Array.isArray(latest.lineItems)) throw new Error('Cart unavailable');
    cart = latest;
    if (!await validateQuickCheckout(confirmed.variantID) || !current()) return false;
    latest = cart;
    const selected = latest.lineItems.find(line => line.id === confirmed.lineId && line.variant?.id === confirmed.variantID && Number(line.quantity) > 0 && editorialAttributesKey(line.customAttributes || []) === editorialAttributesKey(confirmed.attributes));
    if (!selected || cartHasUnavailableItems(latest)) {
      if (!selected || selected.variant?.availableForSale === false) editorialConfirmations.delete(footer);
      if (selected?.variant?.availableForSale === false) nativePurchaseAvailability(product, false);
      throw new Error('Selected cart line unavailable');
    }
    if (!current()) return false;
    const url = cartCheckoutUrl(latest);
    if (!url) throw new Error('Checkout unavailable');
    try {
      if (window.gtag) window.gtag('event', 'begin_checkout', {
        currency: latest.currencyCode, value: Number(latest.subtotalPrice?.amount),
        items: latest.lineItems.map(line => ({
          ...merchantItemIdentity(line.variant.product, line.variant, {
            ...browserMerchantTrackingContext(),
            ...(line.variant.id === confirmed.variantID ? { productHandle: product.dataset.product, brand: product.dataset.brand } : {}),
          }),
          item_name: line.title, item_variant: line.variant.title,
          price: Number(line.variant.price.amount), quantity: line.quantity,
        })),
      });
    } catch { /* Optional analytics cannot delay or prevent checkout. */ }
    window.open(url, '_self');
    return true;
  } catch {
    if (editorialFooter(product) === footer && editorialSelection(product).signature === confirmed.signature) nativePurchaseMessage(product, t('general:product.checkoutError'));
    return false;
  } finally {
    product.dataset.purchasePending = 'false';
    nativePurchaseAvailability(product, product.dataset.purchaseAvailable !== 'false');
    renderEditorialPurchase(product);
  }
}

/** Scope purchase recovery to the approved native PDP, never related cards or Rolex. */
function nativePurchaseProduct(): HTMLElement | null {
  if (/(?:^|[/-])rolex(?:[-/]|$)/i.test(window.location.pathname)) return null;
  const page = document.querySelector('main[data-watch-buying="product"], main[data-jewellery-buying="product"]');
  const product = page?.querySelector<HTMLElement>('.js-product');
  if (!product || /rolex/i.test(product.dataset.brand || '') || product.querySelector('.js-request-purchase')) return null;
  return product;
}
function nativePurchaseButtons(product: HTMLElement): HTMLElement[] {
  return Array.from(product.querySelectorAll<HTMLElement>('.js-add-to-cart, .js-buy-now')).filter(button => button.closest('.js-product') === product);
}
function nativePurchaseAvailability(product: HTMLElement, available: boolean) {
  if (editorialFooter(product)?.dataset.optionPending === 'true') available = false;
  product.dataset.purchaseAvailable = String(available);
  nativePurchaseButtons(product).forEach(button => {
    if (available && product.dataset.purchasePending !== 'true') button.removeAttribute('disabled');
    else button.setAttribute('disabled', 'disabled');
  });
  renderEditorialPurchase(product);
}
function nativePurchaseMessage(product: HTMLElement, message: string, error = true) {
  let status = product.querySelector<HTMLElement>('.js-purchase-status');
  if (!status) {
    status = document.createElement('p'); status.className = 'js-purchase-status';
    status.setAttribute('role', 'alert'); status.setAttribute('tabindex', '-1');
    nativePurchaseButtons(product)[0]?.parentElement?.append(status);
  }
  status.setAttribute('role', error ? 'alert' : 'status');
  status.textContent = message; status.hidden = !message;
  renderEditorialPurchase(product, undefined, message, error);
  if (message && error) {
    const footer = editorialFooter(product);
    const footerStatus = footer?.classList.contains('sticky-buy--visible') ? footer.querySelector<HTMLElement>('[data-editorial-status]') : null;
    if (footerStatus) { footerStatus.setAttribute('tabindex', '-1'); footerStatus.focus({ preventScroll: true }); }
    else status.focus();
  }
}
function nativePurchaseRetry(product: HTMLElement, message: string, label: string, retry: () => void) {
  nativePurchaseMessage(product, message);
  const footer = editorialFooter(product);
  const statuses = [product.querySelector<HTMLElement>('.js-purchase-status'), footer?.querySelector<HTMLElement>('[data-editorial-status]')].filter(Boolean) as HTMLElement[];
  statuses.forEach(status => {
    const button = document.createElement('button'); button.type = 'button';
    button.className = 'btn js-purchase-retry'; button.textContent = label;
    button.style.minHeight = '44px'; button.style.margin = '8px 0 0';
    button.addEventListener('click', () => { button.disabled = true; retry(); });
    status.append(button);
  });
}
async function buyButtonClickHandler(variantID: string, customAttributes: CustomAttribute[] = [], quickBuy = false) {
  const product = nativePurchaseProduct();
  if (!product || !nativePurchaseButtons(product).some(button => button.dataset.id === variantID)) return legacyBuyButtonClickHandler(variantID, customAttributes, quickBuy);
  if (product.dataset.purchasePending === 'true' || product.dataset.purchaseAvailable === 'false' || !nativePurchaseButtons(product).some(button => button.dataset.id === variantID && !button.hasAttribute('disabled'))) return false;
  const available = product.dataset.purchaseAvailable !== 'false';
  product.dataset.purchasePending = 'true';
  nativePurchaseAvailability(product, available);
  nativePurchaseButtons(product).forEach(button => { button.classList.add('processing'); button.setAttribute('aria-busy', 'true'); });
  nativePurchaseMessage(product, '');
  renderEditorialPurchase(product, 'adding', '');
  let added = false;
  const editorial = editorialFooter(product);
  const selection = editorial ? editorialSelection(product) : null;
  const titleSignatureDirect = quickBuy && titleSignatureEnabled(product);
  const revision = product.dataset.titleSignatureRevision || '0';
  const editorialCurrent = () => !editorial || editorialFooter(product) === editorial && editorialSelection(product).signature === selection?.signature && (!titleSignatureEnabled(product) || (product.dataset.titleSignatureRevision || '0') === revision);
  const matchesLine = line => line.variant?.id === variantID && (!editorial || editorialAttributesKey(line.customAttributes || []) === editorialAttributesKey(customAttributes));
  try {
    if (titleSignatureDirect) {
      const latest = await client.cart.fetch(cart.id);
      if (!editorialCurrent() || nativePurchaseProduct() !== product) return false;
      if (!latest?.id || !Array.isArray(latest.lineItems) || cartHasUnavailableItems(latest)) throw new Error('Cart unavailable');
      cart = latest;
    }
    const existing = titleSignatureDirect ? cart.lineItems.find(line => matchesLine(line) && Number(line.quantity) > 0) : null;
    const previousQuantity = (cart?.lineItems || []).filter(matchesLine).reduce((total, line) => total + line.quantity, 0);
    const result = existing ? cart : await client.cart.addLineItems(cart.id, [{ variantId: variantID, quantity: 1, customAttributes }]);
    const selectedLine = result?.lineItems?.find(matchesLine);
    const confirmedQuantity = (result?.lineItems || []).filter(matchesLine).reduce((total, line) => total + line.quantity, 0);
    if (!result?.id || !selectedLine || (existing ? confirmedQuantity < previousQuantity : confirmedQuantity <= previousQuantity)) throw new Error('Cart did not confirm selected variant');
    cart = result; added = !existing;
    if (editorial && selection && selectedLine.id && editorialCurrent() && nativePurchaseProduct() === product) {
      editorialConfirmations.set(editorial, { variantID, signature: selection.signature, lineId: selectedLine.id, attributes: customAttributes.map(attribute => ({ ...attribute })) });
    }
    updateCartTabButton();
    // This best-effort work cannot delay or reject the successful purchase action.
    if (added) void buyButtonAnalytics(selectedLine, cart, product).catch(() => undefined);
    if (quickBuy) {
      if (titleSignatureDirect) {
        if (!editorialCurrent() || nativePurchaseProduct() !== product) return false;
        // Transfer the lock synchronously; editorialCheckout refreshes and validates before navigating.
        product.dataset.purchasePending = 'false';
        return await editorialCheckout(product);
      }
      if (!await validateQuickCheckout(variantID) || !editorialCurrent()) return false;
      const checkoutUrl = cartCheckoutUrl(cart);
      if (!checkoutUrl) throw new Error('Checkout unavailable');
      window.open(checkoutUrl, '_self');
    } else {
      nativePurchaseButtons(product).forEach(button => {
        if (button.dataset.id === variantID && button.classList.contains('js-add-to-cart')) {
          button.classList.add('success');
        }
      });
      if (editorialCurrent()) nativePurchaseMessage(product, t('general:product.addedToBag'), false);
    }
    return true;
  } catch {
    if (editorialCurrent()) nativePurchaseMessage(product, t(added ? 'general:product.checkoutError' : 'general:product.purchaseError'));
    return false;
  } finally {
    product.dataset.purchasePending = 'false';
    nativePurchaseButtons(product).forEach(button => { button.classList.remove('processing'); button.setAttribute('aria-busy', 'false'); });
    nativePurchaseAvailability(product, product.dataset.purchaseAvailable !== 'false');
  }
}

/**
 * Variant option change handler.
 * Set selected variant based on selected options.
 * @param name The name of the selected option (e.g. "Material")
 * @param value The value of the selected option (e.g. "Rose Gold")
 * @param product The product object
 * @param quantity The quantity of the selected variant
 * @param selectedOptions The selected options
 */
function setSelectedVariant(
  name: string,
  value: string,
  product: any,
  quantity: number,
  selectedOptions: { name: string; value: string; }[],
) {
  let variants = product.variants;

  let selectedVariant;
  let variantInvalid = false;

  // check if valid options
  // product.variants => variant.selectedOptions

  variants.forEach((variant) => {
    let valid = true;
    for (const option of variant.selectedOptions) {
      const selectedOption = selectedOptions.find(
        (selected) => selected.name === option.name
      );
      if (!selectedOption || selectedOption.value !== option.value) {
        valid = false;
        break;
      }
    }
    if (valid) {
      // Selected options are valid for this variant
      selectedVariant = variant;
    }
  });

  // if not valid, exclude the one we just clicked on (name) and find next valid option
  if (!selectedVariant) {
    variantInvalid = true;
    selectedVariant = variants.find((variant) => {
      return variant.selectedOptions.find(
        (option) => option.name === name && option.value === value
      );
    });
  }

  product.selectedVariant = selectedVariant;

  // since selected variant didn't match we need to update checked selectors to match new variant
  if (variantInvalid) {
    updateCheckedSelectors(product);
  }

  updateAddToCartBtn(product);

  const selectedVariants = document.querySelectorAll<HTMLInputElement>(
    '.js-variant-selector input:checked',
  );

  selectedVariants.forEach((selectedVariant) => {
    const name = selectedVariant.dataset.option as string;
    const value = selectedVariant.value;
    const handle = selectedVariant.dataset.product as string;

    client.product.fetchByHandle(handle).then((product) => {
      showAvailableVariants(name, value, product);
    });
  });

  // TODO: we need to update the stock level notices;
  // the existing code from Reids won't work here and we need to grab locations somehow.

  updateProductPageStockStatus(product);

  updateVariantPrice(product, product.selectedVariant, quantity);

  updateVariantSku(product, product.selectedVariant);

  updateURLParams({ v: extractID(product.selectedVariant.id) });

  updateVariantImage(product.selectedVariant.id);
}

/**
 * Updates selectors so they match currently selected variant
 */
function updateCheckedSelectors(product: any) {
  product.selectedVariant.selectedOptions.forEach((option) => {
    const input = document.querySelector<HTMLInputElement>(
      `.js-variant-selector input[data-product="${product.handle}"][data-option="${option.name}"][value="${option.value}"]`,
    );
    if (input) {
      input.classList.remove('disabled');
      input.checked = true;
    }
  });
}

/**
 * Updates add to cart button with selected variant ID
 */
function updateAddToCartBtn(product) {
  // All purchase controls on an activated native watch PDP represent the same
  // selected variant, including its mobile and desktop sticky buttons. Leave
  // the existing non-watch/inquiry flow unchanged and exclude related cards.
  const nativeWatchPage = document.querySelector('main[data-watch-buying="product"], main[data-jewellery-buying="product"]');
  const nativeWatchProduct = nativeWatchPage?.querySelector<HTMLElement>('.js-product');
  if (nativeWatchProduct && nativeWatchProduct.dataset.product === product.handle &&
      !/(?:^|\/)rolex(?:[-/]|$)/i.test(window.location.pathname) &&
      !/rolex/i.test(nativeWatchProduct.dataset.brand || '') &&
      !nativeWatchProduct.querySelector('.js-request-purchase')) {
    nativeWatchProduct.querySelectorAll<HTMLElement>('.js-add-to-cart, .js-buy-now').forEach((button) => {
      if (button.closest('.js-product') === nativeWatchProduct) {
        button.setAttribute('data-id', product.selectedVariant.id);
      }
    });
    const footer = editorialFooter(nativeWatchProduct);
    invalidateTitleSignatureSelection(nativeWatchProduct);
    if (footer) editorialConfirmations.delete(footer);
    renderEditorialPurchase(nativeWatchProduct, undefined, '');
    return;
  }
  const addToCartBtn = document.querySelector(
    `.js-product[data-product="${product.handle}"] .js-add-to-cart`,
  );

  const quickBuyBtn = document.querySelector(
    `.js-product[data-product="${product.handle}"] .js-quick-buy`,
  );

  addToCartBtn?.setAttribute('data-id', product.selectedVariant.id);
  quickBuyBtn?.setAttribute('data-id', product.selectedVariant.id);
}

/**
 * Disable any variant options that aren't available for a product
 */
function showAvailableVariants(name: string, value: string, product: any) {
  if (product.options && product.options.length > 1) {
    // grab all variant selector inputs for current product that aren't the ones we just changed

    const allOtherInputs = document.querySelectorAll<HTMLInputElement>(
      `.js-variant-selector input[data-product="${product.handle}"]:not([data-option="${name}"])`,
    );

    let validOptions: {
      name: string;
      value: string;
    }[] = [];

    product.variants.forEach((variant) => {
      if (
        variant.selectedOptions.some(
          (option) => option.name === name && option.value === value
        )
      ) {
        variant.selectedOptions.forEach((option) => {
          // make sure its not the option we just clicked and that its in stock
          if (option.name !== name) {
            validOptions.push({
              name: option.name,
              value: option.value,
            });
          }
        });
      }
    });

    // visually disable any inputs that aren't valid
    allOtherInputs.forEach((input) => {
      if (
        !validOptions.some(
          (option) => (option.name === input.dataset.option) && (option.value === input.value)
        )
      ) {
        input.classList.add('disabled');
      } else {
        input.classList.remove('disabled');
      }
    });
  }
}

/**
 * Update product price based on variant
 */
function updateVariantPrice(product: any, variant: any, quantity = 1) {
  // console.debug('updateVariantPrice', product, variant, quantity);
  if (!variant) {
    return;
  }

  const priceEls = document.querySelectorAll<HTMLElement>(
    `.js-product[data-product="${product.handle}"] .js-product-price`,
  );

  let priceMarkup = '';
  // If has a different "compare-at" price, show that
  if (
    variant.compareAtPrice?.amount &&
    variant.price.amount !== variant.compareAtPrice.amount
  ) {
    priceMarkup += `<strike>${formatAsMoney(
      variant.compareAtPrice.amount * quantity,
      getPageLang(),
    )}</strike>`;
  }
  priceMarkup += `<span>${formatAsMoney(
    variant.price.amount * quantity,
    getPageLang(),
  )}</span>`;

  // Update prices on page
  priceEls.forEach((price) => {
    price.dataset.price = (variant.price.amount * quantity).toString();
    price.innerHTML = priceMarkup;
  });
  const nativeProduct = nativePurchaseProduct();
  if (nativeProduct && nativeProduct.dataset.product === product.handle) {
    const shipping = titleSignatureEnabled(nativeProduct) && nativeProduct.querySelector<HTMLElement>('[data-title-signature-shipping]');
    if (shipping) shipping.dataset.shippingPrice = (variant.price.amount * quantity).toString();
    renderEditorialPurchase(nativeProduct);
  }
}

/**
 * Update product sku based on variant
 */
function updateVariantSku(product, variant) {
  if (variant) {
    // console.debug(variant);
    let sku = document.querySelector(`.js-product[data-product="${product.handle}"] .js-sp-sku`);
    if (sku) {
      sku.innerHTML = variant.publicSku || variant.sku;
    }
    const native = nativePurchaseProduct();
    if (native && native.dataset.product === product.handle) {
      const reference = editorialFooter(native)?.querySelector('[data-editorial-reference]');
      if (reference) reference.textContent = variant.publicSku || variant.sku || '';
    }

    // Update any sku fields on the page
    const skuFields = document.querySelectorAll<HTMLInputElement>('input.js-update-with-variant[name="sku"]');
    skuFields.forEach((field) => {
      field.value = variant.publicSku || variant.sku;
    });
  }
}

/**
 * Update store availability banner
 * @param locations
 * @param available If the item is available in store
 */
function setStoreAvailability(locations: any[], available: boolean) {
  const storeAvailability = document.querySelector<HTMLElement>('.js-store-availability');
  if (!storeAvailability) {
    return;
  }

  storeAvailability.classList.toggle(
    'single-product__notice--availability',
    available,
  );

  const storeAvailabilityText = storeAvailability.querySelector('p') as HTMLElement;

  if (available) {
    const locationLinks = locations
      .map((location) => {
        const url = stardust.route(`${getPageLang()}.contentful.slug`, { '*': ['contact-us'] });
        return `<a href="${url}">${location.location}</a>`;
      });

    // NOTE: The ICU format doesn't specify list formatting, so we need to do it manually
    const formattedLocations = formatList(locationLinks, i18next.language);
    storeAvailabilityText.innerHTML = t('general:product.availableAt', { locations: formattedLocations });
  } else {
    storeAvailabilityText.innerHTML = t('general:product.notAvailableInStore');
  }
}

/**
 * Update online availability banner
 * @param available If the item is available online
 */
function setOnlineAvailability(available: boolean) {
  const onlineAvailability = document.querySelector<HTMLElement>('.js-online-availability');
  if (!onlineAvailability) {
    return;
  }

  onlineAvailability.classList.toggle(
    'single-product__notice--availability',
    available,
  );

  const onlineAvailabilityText = onlineAvailability.querySelector('p') as HTMLElement;

  if (available) {
    onlineAvailabilityText.innerHTML = t('general:product.immediateShipping');
  } else {
    onlineAvailability.classList.add('single-product__notice--info');
    onlineAvailabilityText.innerHTML = t('general:product.notOnline');
  }
}

/**
 * Check stock levels and update page elements
 */
function updateProductPageStockStatus(product) {
  if (!document.querySelector('main.product')) {
    return;
  }

  // `hidePrice` is from our Shopify model.
  // It's added as a data attribute on the product page
  // because we don't have tags in the JS model from Shopify.
  const nativeProduct = nativePurchaseProduct();
  const hidePrice = nativeProduct ? nativeProduct.dataset.priceHidden === 'true'
    : !!document.querySelector('.js-product[data-price-hidden="true"]');

  const stockUrl = stardust.route('api.shop.stock', undefined, { qs: {
    id: product.selectedVariant.id,
    lang: getPageLang(),
  } });

  const selectedId = product.selectedVariant.id;
  const editorial = nativeProduct && editorialFooter(nativeProduct);
  if (editorial) editorial.dataset.stockLoading = 'true';
  if (nativeProduct) { nativeProduct.dataset.stockVariant = selectedId; nativePurchaseAvailability(nativeProduct, false); nativePurchaseMessage(nativeProduct, ''); }

  fetch(stockUrl, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then((response) => response.json())
    .then((response) => {
      if (nativeProduct && nativeProduct.dataset.stockVariant !== selectedId) return;
      if (editorial) editorial.dataset.stockLoading = 'false';
      const inventory = response.result;
      if (!Array.isArray(inventory)) throw new Error('Stock unavailable');

      let locations = inventory.filter((location) => location.available);

      if (locations.length && !hidePrice) {
        enableBuyButtons();
        setStoreAvailability(locations, true);
        setOnlineAvailability(true);
      } else if (locations.length && hidePrice) {
        disableBuyButtons();
        setStoreAvailability(locations, true);
        setOnlineAvailability(false);
      } else {
        disableBuyButtons();
        // show out of stock messaging
        setStoreAvailability(locations, false);
        setOnlineAvailability(false);
      }
    })
    .catch(() => {
      if (nativeProduct && nativeProduct.dataset.stockVariant === selectedId) {
        if (editorial) editorial.dataset.stockLoading = 'false';
        nativePurchaseAvailability(nativeProduct, false);
        nativePurchaseRetry(nativeProduct, t('general:product.availabilityError'), t('general:product.tryAgain'), () => updateProductPageStockStatus(product));
      }
    });
}

function disableBuyButtons() {
  const product = nativePurchaseProduct();
  if (product) { nativePurchaseAvailability(product, false); return; }
  document.querySelector('.js-add-to-cart')?.setAttribute('disabled', 'disabled');
  document.querySelector('.js-buy-now')?.setAttribute('disabled', 'disabled');
}

function enableBuyButtons() {
  const product = nativePurchaseProduct();
  if (product) { nativePurchaseAvailability(product, true); return; }
  document.querySelector('.js-add-to-cart')?.removeAttribute('disabled');
  document.querySelector('.js-buy-now')?.removeAttribute('disabled');
}

async function buyButtonAnalytics(product, cart, productCard) {
  // TODO: convert to GA4
  if (window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: cart.currencyCode,
      items: [
        {
          ...merchantItemIdentity(product.variant.product, product.variant, {
            ...browserMerchantTrackingContext(),
            productHandle: productCard?.getAttribute('data-product') || '',
            brand: productCard?.getAttribute('data-brand') || '',
          }),
          item_name: product.title,
          item_variant: product.variant.title,
          price: product.variant.price.amount,
          currency: cart.currencyCode,
          quantity: 1,
        },
      ],
      value: product.variant.price.amount,
    });
  }

  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      currency: cart.currencyCode,
      items: [
        {
          item_id: product.variant.sku,
          item_name: product.title,
          item_variant: product.variant.title,
          price: product.variant.price.amount,
          currency: cart.currencyCode,
          quantity: 1,
        },
      ],
      value: product.variant.price.amount,
    });
  }

  // https://www.algolia.com/doc/api-reference/api-methods/added-to-cart-object-ids-after-search/
  // https://www.algolia.com/doc/api-reference/api-methods/added-to-cart-object-ids/

  // TODO: There's probably a better way to grab the index name, but this works for now
  const indexName = document.querySelector<HTMLElement>('.sortBy__button--selected')?.dataset.value;
  const price = parseFloat(product.variant.price.amount);
  const urlParams = new URLSearchParams(window.location.search);
  const queryID = urlParams.get('qid') || productCard?.getAttribute('data-query-id') || null;
  let objectID = urlParams.get('oid') || productCard?.getAttribute('data-object-id') || null;

  if (!objectID) {
    const handle = document.querySelector<HTMLElement>('.js-product')?.getAttribute('data-product');
    let objectIdCall = await fetch(stardust.route('api.shop.objectId', undefined, { qs: {
      handle,
      variantTitle: product.variant.title,
      lang: getPageLang(),
    },
    }));
    objectID = await objectIdCall.json().then((data) => data.objectId);
  }

  if (!queryID && !objectID) {
    console.debug('No queryID/objectID found for Algolia insights tracking');
    return;
  }

  const insightsData = {
    userToken: aa('getUserToken')?.toString(),
    eventName: 'Added to Cart',
    index: indexName ?? DEFAULT_INDEX,
    objectIDs: [objectID],
    objectData: [{
      price,
      quantity: 1,
    }],
    value: price,
    currency: cart.currencyCode,
  };

  if (queryID) {
    // Have query and object ID, from a search/archive
    await aa('addedToCartObjectIDsAfterSearch', {
      ...insightsData,
      queryID,
    });
  } else {
    // No query ID, but have object ID (fetched above)
    await aa('addedToCartObjectIDs', insightsData);
  }

  // Dual-write to GA4 so we own the query→conversion data (see search-events.ts)
  trackSearchEvent('algolia_add_to_cart', {
    object_id: objectID,
    query_id: queryID,
    price,
    currency: cart.currencyCode,
    surface: 'cart',
  });
}

function updateURLParams(params) {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  for (const key in params) {
    urlParams.set(key, params[key]);
  }
  window.history.replaceState({}, '', `${location.pathname}?${urlParams}`);

  // Update any URL fields on the page
  const urlFields = document.querySelectorAll<HTMLInputElement>('input.js-update-with-variant[name="url"]');
  urlFields.forEach((field) => {
    field.value = window.location.href;
  });
}

export default {
  buyButtonClickHandler,
};

export { buyButtonClickHandler };
